import { ProductDetails, Callout } from '@sh24/ui-components'
import { getProp } from '@sh24/contentful-service/utils'
import styled from 'styled-components'
import documentToReactComponents from '../../utils/document-to-react-components'
import Image from '../Image/image'
import buildBreadcrumbLinks from '../../utils/build-breadcrumb-links'
import { formatPrice } from '../../utils/helpers'
import useTranslations from '../../utils/use-translations'

const DiscountContent = styled.div`
  display: flex;
  justify-content: space-between;
`

const ProductHero = ({ productPage, context, send }) => {
  const translations = useTranslations()
  const backgroundColour = getProp(productPage, 'heroBackgroundColour') || ''
  const highlights = getProp(productPage, 'highlights')
  const locale = getProp(context, 'locale')
  const currency = getProp(context, 'currency')
  const minPrice = getProp(productPage?.data?.minPrice, currency)
  const discount = getProp(context, 'discount')
  const selectedPrice = getProp(context, 'selectedPrice')
  const selectedTotal = selectedPrice + discount
  const buttonText = getProp(context, 'orderButtonText')
  const outOfStockButtonText = getProp(context, 'oosOrderButtonText')
  const options = getProp(context, 'options').filter((option) => option.inStock)
  const hasSelectedOptions = options.filter((option) => option.checked).length > 0
  const inStock = getProp(productPage.data, 'inStock')
  const showOptions = options.length > 1 && inStock
  const isBundle = getProp(productPage.data, 'isBundle')

  const HeroImage = <Image image={getProp(productPage, 'image')} maxWidth={640} maxHeight={640} priority />

  const Highlights =
    highlights &&
    highlights.map((highlight) => {
      const Content = documentToReactComponents(highlight.content)
      const HighlightImage = <Image image={highlight.image} maxWidth={97} maxHeight={97} responsive={false} />

      return {
        id: highlight.id,
        contentType: highlight.contentType,
        content: Content,
        image: HighlightImage,
      }
    })

  const discountCallout =
    discount !== 0 ? (
      <Callout
        key="discount-callout"
        backgroundColour="/white"
        iconBackgroundColour="/secondary200"
        icon="percent"
        content={
          <>
            <DiscountContent>
              <span>{translations['productPage.originalPrice']}</span>
              <span className="heading heading-3">{formatPrice({ price: selectedPrice, locale, currency })}</span>
            </DiscountContent>
            <DiscountContent>
              <span className="semi-bold">{translations['productPage.multiTestDiscount']}</span>
              <span className="heading heading-3">{formatPrice({ price: discount, locale, currency })}</span>
            </DiscountContent>
          </>
        }
      />
    ) : null

  const beforeOptionsContent = documentToReactComponents(getProp(productPage, 'beforeOptionsContent'))
  const legend = getProp(productPage, 'legend')
  const beforeOrderContent = [documentToReactComponents(getProp(productPage, 'beforeOrderContent')), discountCallout]
  const afterOrderContent = documentToReactComponents(getProp(productPage, 'afterOrderContent'))
  const beforePriceText = showOptions && !isBundle ? getProp(context, 'beforePriceText') : null
  const headerPrice = isBundle ? selectedTotal : minPrice
  const formattedHeaderPrice = formatPrice({ price: headerPrice, locale, currency })
  const formattedSelectedTotal = formatPrice({ price: selectedTotal, locale, currency })
  const orderButtonText = selectedPrice ? `${buttonText} ${formattedSelectedTotal}` : buttonText
  const breadcrumbLinks = buildBreadcrumbLinks(productPage)

  return (
    <ProductDetails
      backgroundColour={backgroundColour}
      decoration={getProp(productPage, 'heroDecoration')}
      image={HeroImage}
      title={getProp(productPage, 'title')}
      type={getProp(productPage, 'type')}
      beforePriceText={beforePriceText}
      price={formattedHeaderPrice}
      underPriceText={getProp(productPage, 'underPriceText')}
      highlights={Highlights}
      beforeOptionsContent={beforeOptionsContent}
      legend={legend}
      options={options}
      asCheckboxes={getProp(context, 'checkboxes')}
      onChange={(e) => send('SELECT_OPTION', { payload: e.target.value })}
      beforeOrderContent={beforeOrderContent}
      orderButtonText={inStock ? orderButtonText : outOfStockButtonText}
      onOrder={() => send('ORDER')}
      afterOrderContent={afterOrderContent}
      isInStock={inStock}
      isBundle={isBundle}
      isOrderButtonDisabled={!inStock || !hasSelectedOptions}
      canBeOrderedDirectly={getProp(productPage, 'canBeOrderedDirectly')}
      showOptions={showOptions}
      breadcrumbLinks={breadcrumbLinks}
    />
  )
}

export default ProductHero
